/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { forwardRef, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';
import { MoveFocusInside } from 'react-focus-on';

import { Button } from 'uikit/buttons';

import styles from '../styles/uikitLayoutsDrawer.module.scss';
import { useEscKeyHandler } from '../../utils';

const Drawer = forwardRef(
  (
    {
      ariaLabel = '',
      ariaLabelledby = '',
      children = null,
      className = '',
      hasCloseButton = false,
      id = 'drawer',
      isActive = false,
      isSocial = true,
      onCloseButtonClick = () => {},
      placement = 'left',
      position = 'fixed',
      testId = '',
      triggerRef = null,
    },
    ref,
  ) => {
    const PLACEMENT = {
      left: styles.isPlacementLeft,
      right: styles.isPlacementRight,
    };

    const POSITION = {
      absolute: styles.isPositionAbsolute,
      fixed: styles.isPositionFixed,
    };

    useEscKeyHandler(onCloseButtonClick, hasCloseButton && isActive);

    useEffect(() => {
      if (triggerRef && !isActive) triggerRef.current?.focus();
    }, [isActive, triggerRef]);

    const WrapperElement = isActive ? MoveFocusInside : Fragment;

    const ariaLabelProps = ariaLabelledby
      ? { 'aria-labelledby': ariaLabelledby }
      : { 'aria-label': ariaLabel };

    return (
      <WrapperElement>
        <aside
          {...ariaLabelProps}
          aria-hidden={!isActive}
          className={classnames(
            styles.element,
            {
              [styles.hasCloseButton]: hasCloseButton,
              [styles.isActive]: isActive,
              [PLACEMENT[placement]]: placement,
              [POSITION[position]]: position,
            },
            className,
          )}
          data-testid={testId}
          id={id}
          role="dialog"
          ref={ref}
        >
          {hasCloseButton && (
            <div className={styles.closeButtonContainer}>
              <Button
                aria-label={i18n.t('close', 'Close')}
                icon="close"
                onClick={onCloseButtonClick}
                size="small"
                variant="ghost"
              />
            </div>
          )}
          {!isSocial ? <div className={styles.content}>{children}</div> : children}
        </aside>
      </WrapperElement>
    );
  },
);

Drawer.propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  hasCloseButton: PropTypes.bool,
  id: PropTypes.string,
  isActive: PropTypes.bool,
  isSocial: PropTypes.bool,
  onCloseButtonClick: PropTypes.func,
  placement: PropTypes.oneOf(['left', 'right']),
  position: PropTypes.oneOf(['absolute', 'fixed']),
  testId: PropTypes.string,
  triggerRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.oneOf([null])]),
  }),
};

export default Drawer;
